import socketService from './socket';

const ICE_SERVERS = {
  iceServers: [
    { urls: 'stun:stun.l.google.com:19302' },
    { urls: 'stun:stun1.l.google.com:19302' },
    { urls: 'stun:stun2.l.google.com:19302' },
    { urls: 'stun:stun3.l.google.com:19302' },
    { urls: 'stun:stun4.l.google.com:19302' },
  ],
  iceCandidatePoolSize: 10,
};

class WebRTCService {
  constructor() {
    this.peerConnections = new Map();
    this.localStream = null;
    this.screenStream = null;
    this.remoteStreams = new Map();

    this.onRemoteStream = null;
    this.onPeerDisconnected = null;

    this.iceCandidateTimeouts = new Map();
    this.negotiationState = new Map();

    // 🔥 FIX: buffer ICE candidates
    this.pendingCandidates = new Map();

    // 🔥 FIX: prevent multiple getUserMedia calls
    this.localStreamPromise = null;
  }

  async getLocalStream(constraints = { video: true, audio: true }) {
    if (this.localStream) return this.localStream;

    if (this.localStreamPromise) return this.localStreamPromise;

    if (!navigator.mediaDevices?.getUserMedia) {
      console.error('getUserMedia not available: mediaDevices API is not supported in this context (requires HTTPS or localhost)');
      return null;
    }

    this.localStreamPromise = navigator.mediaDevices
      .getUserMedia(constraints)
      .then((stream) => {
        this.localStream = stream;
        this.localStreamPromise = null;
        return stream;
      })
      .catch((err) => {
        this.localStreamPromise = null;
        console.error('Error getting local stream:', err);
        return null;
      });

    return this.localStreamPromise;
  }

  async getScreenStream() {
    try {
      this.screenStream = await navigator.mediaDevices.getDisplayMedia({
        video: true,
        audio: false,
      });
      return this.screenStream;
    } catch (error) {
      console.error('Error getting screen stream:', error);
      throw error;
    }
  }

  stopScreenStream() {
    if (this.screenStream) {
      this.screenStream.getTracks().forEach((t) => t.stop());
      this.screenStream = null;
    }
  }

  createPeerConnection(peerId) {
    if (this.peerConnections.has(peerId)) {
      console.log('[WebRTC] Reusing PeerConnection:', peerId, 'state=', this.peerConnections.get(peerId).signalingState);
      return this.peerConnections.get(peerId);
    }

    const pc = new RTCPeerConnection(ICE_SERVERS);
    const polite = this.getPeerPoliteness(peerId);

    this.negotiationState.set(peerId, {
      makingOffer: false,
      haveRemoteOffer: false,
      polite,
    });

    console.log('[WebRTC] Creating PeerConnection:', peerId, 'polite=', polite);

    // Add local tracks
    if (this.localStream) {
      this.localStream.getTracks().forEach((track) => {
        pc.addTrack(track, this.localStream);
      });
    }

    pc.onicecandidate = (event) => {
      if (event.candidate) {
        console.log('[WebRTC] ICE candidate generated:', peerId, event.candidate.candidate);
        socketService.sendIceCandidate(peerId, event.candidate);
      }
    };

    pc.onsignalingstatechange = () => {
      this.logConnectionState(peerId, pc, 'signalingState change');
    };

    pc.onconnectionstatechange = () => {
      this.logConnectionState(peerId, pc, 'connectionState change');
    };

    pc.ontrack = (event) => {
      let stream = event.streams?.[0];

      if (!stream && event.track) {
        stream = new MediaStream([event.track]);
      }

      if (stream) {
        this.remoteStreams.set(peerId, stream);

        if (this.onRemoteStream) {
          this.onRemoteStream(peerId, stream);
        }
      }
    };

    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState;

      if (['failed', 'closed'].includes(state)) {
        this.logConnectionState(peerId, pc, 'ICE connection terminal state');
        this.remoteStreams.delete(peerId);
        this.peerConnections.delete(peerId);
        this.negotiationState.delete(peerId);
        this.pendingCandidates.delete(peerId);

        if (this.onPeerDisconnected) {
          this.onPeerDisconnected(peerId);
        }
      }
    };

    this.peerConnections.set(peerId, pc);
    this.logConnectionState(peerId, pc, 'PeerConnection created');
    return pc;
  }

  getNegotiationState(peerId) {
    if (!this.negotiationState.has(peerId)) {
      this.negotiationState.set(peerId, {
        makingOffer: false,
        haveRemoteOffer: false,
        polite: this.getPeerPoliteness(peerId),
      });
    }

    return this.negotiationState.get(peerId);
  }

  getPeerPoliteness(peerId) {
    const socketId = socketService.getSocketId();

    if (!socketId) {
      return true;
    }

    return socketId > peerId;
  }

  logConnectionState(peerId, pc, label) {
    console.log('[WebRTC]', label, {
      peerId,
      signalingState: pc?.signalingState || 'closed',
      connectionState: pc?.connectionState || 'closed',
      iceConnectionState: pc?.iceConnectionState || 'closed',
      iceGatheringState: pc?.iceGatheringState || 'complete',
      activePeerConnections: this.peerConnections.size,
    });
  }

  // 🔥 FIX: OFFER SAFE STATE
  async createOffer(peerId) {
    const pc = this.createPeerConnection(peerId);
    const state = this.getNegotiationState(peerId);

    try {
      if (state.makingOffer) {
        console.warn('[WebRTC] Skip duplicate offer', peerId, 'already making offer');
        return;
      }

      if (pc.signalingState !== 'stable') {
        console.warn(`[WebRTC] Skip offer ${peerId}, state=${pc.signalingState}`);
        return;
      }

      state.makingOffer = true;
      console.log('[WebRTC] Creating offer:', peerId, 'state=', pc.signalingState);

      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
      });

      await pc.setLocalDescription(offer);
      console.log('[WebRTC] Local offer set:', peerId, 'state=', pc.signalingState);
      this.logConnectionState(peerId, pc, 'Offer localDescription set');

      socketService.sendOffer(peerId, pc.localDescription);
      console.log('[WebRTC] Offer sent:', peerId, 'type=', pc.localDescription?.type);
    } catch (err) {
      console.error('[WebRTC] createOffer error:', peerId, err);
    } finally {
      state.makingOffer = false;
    }
  }

  // 🔥 FIX: OFFER HANDLING SAFE
  async handleOffer(peerId, offer) {
    const pc = this.createPeerConnection(peerId);
    const state = this.getNegotiationState(peerId);

    try {
      const readyForOffer =
        !state.makingOffer &&
        (pc.signalingState === 'stable' || state.haveRemoteOffer);

      const offerCollision =
        offer.type === 'offer' &&
        !readyForOffer;

      console.log('[WebRTC] Received offer:', peerId, 'type=', offer.type, 'state=', pc.signalingState, 'polite=', state.polite, 'collision=', offerCollision);

      const ignoreOffer = !state.polite && offerCollision;

      if (ignoreOffer) {
        console.warn('[WebRTC] Ignoring offer collision:', peerId, 'state=', pc.signalingState, 'polite=', state.polite);
        return;
      }

      if (offerCollision) {
        console.warn('[WebRTC] Rollback local offer:', peerId, 'state=', pc.signalingState, 'polite=', state.polite);
        await pc.setLocalDescription(null);
        console.log('[WebRTC] Local description rolled back:', peerId, 'state=', pc.signalingState);
      }

      state.haveRemoteOffer = true;
      await pc.setRemoteDescription(new RTCSessionDescription(offer));
      console.log('[WebRTC] Remote offer set:', peerId, 'state=', pc.signalingState);

      const answer = await pc.createAnswer();
      console.log('[WebRTC] Answer created:', peerId, 'state=', pc.signalingState);
      await pc.setLocalDescription(answer);
      console.log('[WebRTC] Local answer set:', peerId, 'state=', pc.signalingState);
      this.logConnectionState(peerId, pc, 'Answer localDescription set');

      socketService.sendAnswer(peerId, pc.localDescription);
      console.log('[WebRTC] Answer sent:', peerId, 'type=', pc.localDescription?.type);

      // 🔥 flush ICE
      this.flushPendingCandidates(peerId, pc);
    } catch (err) {
      console.error('[WebRTC] handleOffer error:', peerId, err);
    } finally {
      state.haveRemoteOffer = false;
    }
  }

  async handleAnswer(peerId, answer) {
    const pc = this.peerConnections.get(peerId);
    if (!pc) return;

    try {
      if (pc.signalingState === 'have-local-offer') {
        console.log('[WebRTC] Received answer:', peerId, 'state=', pc.signalingState);
        await pc.setRemoteDescription(new RTCSessionDescription(answer));
        console.log('[WebRTC] Remote answer set:', peerId, 'state=', pc.signalingState);
        this.logConnectionState(peerId, pc, 'Answer remoteDescription set');

        // 🔥 flush ICE
        this.flushPendingCandidates(peerId, pc);
      } else {
        console.warn('[WebRTC] Ignore answer:', peerId, 'state=', pc.signalingState);
      }
    } catch (err) {
      console.error('[WebRTC] handleAnswer error:', peerId, err);
    }
  }

  // 🔥 FIX: ICE BUFFERING STABLE
  async handleIceCandidate(peerId, candidate) {
    const pc = this.peerConnections.get(peerId);
    if (!pc) return;

    if (!pc.remoteDescription) {
      if (!this.pendingCandidates.has(peerId)) {
        this.pendingCandidates.set(peerId, []);
      }

      this.pendingCandidates.get(peerId).push(candidate);
      console.log('[WebRTC] ICE candidate buffered:', peerId, candidate.candidate || 'null-candidate');
      return;
    }

    try {
      console.log('[WebRTC] Adding ICE candidate:', peerId, candidate.candidate || 'null-candidate');
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (err) {
      console.error('[WebRTC] ICE error:', peerId, err);
    }
  }

  flushPendingCandidates(peerId, pc) {
    const pending = this.pendingCandidates.get(peerId);

    if (pending?.length) {
      console.log('[WebRTC] Flushing ICE candidates:', peerId, 'count=', pending.length);
      pending.forEach((c) => {
        pc.addIceCandidate(new RTCIceCandidate(c)).catch((err) => {
          console.error('[WebRTC] Flush ICE error:', peerId, err);
        });
      });

      this.pendingCandidates.delete(peerId);
    }
  }

  closePeerConnection(peerId) {
    const pc = this.peerConnections.get(peerId);
    if (pc) {
      console.log('[WebRTC] Closing PeerConnection:', peerId);
      pc.close();
    }

    this.peerConnections.delete(peerId);
    this.remoteStreams.delete(peerId);
    this.negotiationState.delete(peerId);

    if (this.pendingCandidates.has(peerId)) {
      this.pendingCandidates.delete(peerId);
    }

    console.log('[WebRTC] Active PeerConnections after close:', this.peerConnections.size);
  }

  closeAllConnections() {
    console.log('[WebRTC] Closing all PeerConnections:', this.peerConnections.size);
    this.peerConnections.forEach((pc, peerId) => {
      console.log('[WebRTC] Closing PeerConnection:', peerId);
      pc.close();
    });
    this.peerConnections.clear();
    this.remoteStreams.clear();
    this.pendingCandidates.clear();
    this.negotiationState.clear();
    console.log('[WebRTC] Active PeerConnections after cleanup:', this.peerConnections.size);
  }

  stopLocalStream() {
    if (this.localStream) {
      this.localStream.getTracks().forEach((t) => t.stop());
      this.localStream = null;
    }
  }

  toggleAudio(muted) {
    this.localStream?.getAudioTracks().forEach((t) => {
      t.enabled = !muted;
    });
  }

  toggleVideo(off) {
    this.localStream?.getVideoTracks().forEach((t) => {
      t.enabled = !off;
    });
  }

  replaceVideoTrack(newTrack) {
    this.peerConnections.forEach((pc) => {
      const sender = pc.getSenders().find((s) => s.track?.kind === 'video');
      if (sender) sender.replaceTrack(newTrack);
    });
  }

  cleanup() {
    this.closeAllConnections();
    this.stopLocalStream();
    this.stopScreenStream();
  }
}

const webrtcService = new WebRTCService();
export default webrtcService;